/**
 * One email composer, for any surface that sends an email as the viewer: the From / To / Cc / Subject grid of the
 * Drips draft pane, a '/' snippet body with its fields to fill, attachments, the signature line, and Send with its
 * states (sending with upload progress, failed with Try Again and Copy Email, not connected, paused). The deal
 * timeline is its first caller (`deal-command-center/deal-email-composer.tsx`); the Drips draft pane and the SOP
 * `email.send` dialog can adopt it later.
 *
 * The caller holds the draft (`draft` / `onDraftChange`), so it outlives the composer: a reply inside a timeline card
 * keeps its words and files when the card folds or a filter hides it. The caller also owns the send: it gets the
 * draft from `onSend`, uploads the files, reports each file's progress back through `progress`, and sets `phase` and
 * `error`. After a send attempt the email is frozen: Try Again sends exactly what was attempted, and `onEditAsNew`
 * starts a separate send for any change, so an edit is never reported as sent when the first attempt went out.
 */

import { type ReactNode, useState } from "react";
import { AlertCircle, CheckIcon, CopyIcon, FileText, Loader2Icon, Mail, SendIcon, X } from "lucide-react";
import { toast } from "sonner";

import { SuppressionNote } from "@/components/contact-pauses/suppression-note";
import { CcInput } from "@/components/drips/cc-input";
import { ConnectOutlookButton } from "@/components/stock-to-proposal/connect-outlook";
import { FieldsToFill, SnippetTextarea } from "@/components/snippets/snippet-picker";
import { AttachFileButton } from "@/components/tickets/attach-file-button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveOverlay } from "@/components/ui/responsive-overlay";
import { COMPOSER_BUTTON, COMPOSER_FIELD } from "@/components/todos/todo-composer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { SuppressionInfo } from "@/hooks/use-contact-pauses";
import { allowHref } from "@/hooks/use-kory-access";
import { emptyFields } from "@/lib/snippet-fields";
import { formatBytes } from "@/lib/ticket-attachments";
import { TAP_AREA } from "@/lib/tap-area";
import { cn } from "@/lib/utils";

import { MailSignature, type MailSignatureData } from "./mail-signature";

/** The mailbox a send goes out from: the most connected one the server picked (Outlook, then the Gmail grant, then
 * the company's Google delegation). Anything but `ready` carries `message`, its one plain sentence. */
export interface SendMailbox {
  provider: "gmail" | "outlook" | "gmail_dwd";
  state: "ready" | "connect" | "expired" | "no_send_permission";
  address: string | null;
  /** Gmail's grant link. */
  connect_url?: string | null;
  message?: string;
  /** Ready, but not the mailbox it would be: why (e.g. the brand's Outlook mailbox needs Reconnect to send). */
  note?: string;
}

/** "Outlook" or "Gmail": the mailbox's name as people know it. */
export function mailboxName(mailbox: Pick<SendMailbox, "provider">): string {
  return mailbox.provider === "outlook" ? "Outlook" : "Gmail";
}

/** Who else the composer may send as: the deal's shown owner, when the viewer may send as them. */
export interface SendAsOwner {
  name: string;
  email: string;
  /** Not `undefined`: a state that cannot send disables the option, with why. */
  mailbox: SendMailbox;
}

/** Connect or Reconnect for the viewer's own mailbox. With nothing connected, Gmail's grant link and, when the
 * viewer may, Connect Outlook: either one makes Send work. */
function ConnectMailbox({ mailbox, outlookConnect }: { mailbox: SendMailbox; outlookConnect: boolean }) {
  const verb = mailbox.state === "connect" ? "Connect" : "Reconnect";
  if (mailbox.provider === "outlook") return <ConnectOutlookButton label={`${verb} Outlook`} />;
  return (
    <div className="flex flex-wrap gap-2">
      {mailbox.connect_url ? (
        <Button asChild variant="outline" size="sm" className="pointer-coarse:h-11">
          <a href={allowHref(mailbox.connect_url)}>{verb} Gmail</a>
        </Button>
      ) : null}
      {mailbox.state === "connect" && outlookConnect ? <ConnectOutlookButton label="Connect Outlook" /> : null}
    </div>
  );
}

/** Gmail's own limits: 25 MB of attachments in one email. Ten files is ours, and the server checks both again. */
export const MAX_FILES = 10;
export const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
/** An Outlook send carries its files in one request to Microsoft: 3 MB in all. The server checks it again. */
export const OUTLOOK_MAX_TOTAL_BYTES = 3 * 1024 * 1024;
/** The send endpoint's own limits. */
export const MAX_SUBJECT = 300;
export const MAX_BODY = 20_000;
/** Extensions Gmail refuses to attach, mirrored from the send endpoint so a refused file never uploads. */
const BLOCKED_EXTENSIONS = new Set(
  (
    "ade adp apk appx appxbundle bat cab chm cmd com cpl dll dmg ex ex_ exe hta ins isp iso jar jse lib lnk mde msc " +
    "msi msix msixbundle msp mst nsh pif ps1 scr sct shb sys vb vbe vbs vxd wsc wsf wsh"
  ).split(" "),
);

export function blockedByGmail(filename: string): boolean {
  const dot = filename.lastIndexOf(".");
  return dot !== -1 && BLOCKED_EXTENSIONS.has(filename.slice(dot + 1).toLowerCase());
}

export interface ComposerFile {
  id: string;
  file: File;
}

export interface EmailDraft {
  subject: string;
  body: string;
  cc: string[];
  files: ComposerFile[];
}

export type ComposerPhase = "editing" | "sending" | "failed";

export const EMPTY_DRAFT: EmailDraft = { subject: "", body: "", cc: [], files: [] };

/** "To: …\nCc: …\nSubject: …\n\nbody": what Copy Email puts on the clipboard. */
export function emailAsText(to: string, draft: Pick<EmailDraft, "subject" | "body" | "cc">): string {
  return [`To: ${to}`, draft.cc.length ? `Cc: ${draft.cc.join(", ")}` : null, `Subject: ${draft.subject}`, "", draft.body]
    .filter((line) => line !== null)
    .join("\n");
}

export interface EmailComposerProps {
  /** A new email has its own header and a Cc box. A reply sits inside its thread: no header, no Cc, its subject fixed. */
  variant: "new" | "reply";
  /** The sender, as "Name · address". Null while it loads. */
  from: string | null;
  to: { name: string | null; address: string };
  draft: EmailDraft;
  onDraftChange: (draft: EmailDraft) => void;
  /** Snippets: the record's brand, and the fields the record already knows. */
  snippets: { tenantId: string | null | undefined; brandName?: string | null; fills: Record<string, string | null | undefined> };
  /** The viewer's mailbox. Anything but `ready` shows how to connect it, and Copy Email in place of Send. */
  mailbox: SendMailbox | undefined;
  /** The viewer may start Connect Outlook (it is set up, and they hold the Drips capability). */
  outlookConnect?: boolean;
  signature: MailSignatureData | undefined;
  /** The deal's shown owner, when the viewer may send as them (the server re-checks on every send
   * regardless of this prop). Undefined/null: the From row stays today's static "you" line. */
  owner?: SendAsOwner | null;
  /** "me" (default) or "owner" — lifted so the caller can default a reply to wherever its thread lives. */
  sendAs?: "me" | "owner";
  onSendAsChange?: (sendAs: "me" | "owner") => void;
  /** A contact pause on the recipient: Send waits, and the note says why. */
  pause: SuppressionInfo | null;
  /** The pause read has not answered yet (or failed): Send waits, since an unknown pause could hold. */
  pauseUnknown?: boolean;
  phase: ComposerPhase;
  /** Why the last send stopped, in the words to show. */
  error?: string | null;
  /** Each file's upload, 0–1, by `ComposerFile.id`, while sending. */
  progress?: Record<string, number>;
  onSend: (draft: EmailDraft) => void;
  onDiscard: () => void;
  /** After a failed send: leave that attempt as it is and edit the email as a new send. */
  onEditAsNew?: () => void;
  /** A line above the fields about where this send goes (the personal test mode line). */
  notice?: ReactNode;
  /** `sheet`: a phone's full-screen compose. A compact top bar (Cancel, "Email <name>", Send), compact To/Cc/Subject,
   *  and the body filling down to the keyboard. The parent gives it the screen (`PhoneComposeSheet`). */
  layout?: "inline" | "sheet";
  className?: string;
}

/** A label cell of the field grid. */
function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return htmlFor ? (
    <label htmlFor={htmlFor} className="text-muted-foreground">
      {children}
    </label>
  ) : (
    <span className="text-muted-foreground">{children}</span>
  );
}

function FileChip({
  file,
  progress,
  onRemove,
}: {
  file: File;
  progress: number | undefined;
  onRemove: (() => void) | null;
}) {
  return (
    <span className="relative inline-flex max-w-full min-w-0 items-center gap-1.5 overflow-hidden rounded-md border py-1 pr-1 pl-2 text-xs">
      <FileText className="text-muted-foreground size-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">{file.name}</span>
      <span className="text-muted-foreground shrink-0 tabular-nums">{formatBytes(file.size)}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-5 shrink-0"
        onClick={onRemove ?? undefined}
        disabled={!onRemove}
        aria-label={`Remove ${file.name}`}
      >
        <X className="size-3.5" aria-hidden="true" />
      </Button>
      {progress !== undefined ? (
        // The upload, along the chip's bottom edge.
        <span
          role="progressbar"
          aria-label={`Uploading ${file.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          className="bg-muted absolute inset-x-0 bottom-0 h-0.5"
        >
          <span className="bg-primary block h-full origin-left" style={{ transform: `scaleX(${progress})` }} />
        </span>
      ) : null}
    </span>
  );
}

export function EmailComposer({
  variant,
  from,
  to,
  draft,
  onDraftChange,
  snippets,
  mailbox,
  signature,
  owner,
  sendAs = "me",
  onSendAsChange,
  pause,
  pauseUnknown,
  phase,
  error,
  progress,
  onSend,
  onDiscard,
  onEditAsNew,
  notice,
  layout = "inline",
  className,
  outlookConnect = false,
}: EmailComposerProps) {
  const sheet = layout === "sheet";
  const { subject, body, cc, files } = draft;
  const set = (patch: Partial<EmailDraft>) => onDraftChange({ ...draft, ...patch });
  // A refused address still in the Cc box: Send waits until it is fixed or cleared.
  const [ccUnfinished, setCcUnfinished] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmingSendAsOwner, setConfirmingSendAsOwner] = useState(false);

  const sending = phase === "sending";
  // Sent or not, an attempted email is what its send token stands for: it is not edited in place.
  const frozen = phase !== "editing";
  const asOwner = sendAs === "owner" && !!owner;
  const connected = asOwner ? owner.mailbox.state === "ready" : mailbox?.state === "ready";
  const total = files.reduce((sum, f) => sum + f.file.size, 0);
  // The files' limit is the sending mailbox's: Outlook's is smaller, so it is checked before anything uploads.
  const viaOutlook = (asOwner ? owner.mailbox : mailbox)?.provider === "outlook";
  const over = total - (viaOutlook ? OUTLOOK_MAX_TOTAL_BYTES : MAX_TOTAL_BYTES);
  const tooLong = [
    subject.length > MAX_SUBJECT ? `The subject is ${subject.length - MAX_SUBJECT} characters over its ${MAX_SUBJECT} limit.` : null,
    body.length > MAX_BODY ? `The email is ${(body.length - MAX_BODY).toLocaleString()} characters over its ${MAX_BODY.toLocaleString()} limit.` : null,
  ].filter((w): w is string => !!w);
  // A snippet's field nobody filled yet: Send and Copy wait for it.
  const unfilled = emptyFields(body).length > 0;
  const canSend =
    connected &&
    !pause &&
    !pauseUnknown &&
    !sending &&
    !!body.trim() &&
    !!subject.trim() &&
    over <= 0 &&
    !tooLong.length &&
    !unfilled &&
    !ccUnfinished;

  const addFiles = (picked: File[]) => {
    const refused = picked.filter((f) => blockedByGmail(f.name));
    if (refused.length) toast.error(`Gmail blocks this type of file: ${refused.map((f) => f.name).join(", ")}`);
    const allowed = picked.filter((f) => !blockedByGmail(f.name));
    const room = MAX_FILES - files.length;
    if (allowed.length > room) toast.error(`An email takes up to ${MAX_FILES} files.`);
    set({ files: [...files, ...allowed.slice(0, Math.max(0, room)).map((file) => ({ id: crypto.randomUUID(), file }))] });
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(emailAsText(to.address, draft));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (files.length) toast.info("Copied. Attach the files again in Gmail.");
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const reply = variant === "reply";
  // The composers' one button scale (the to-do and note composers' Add); a phone keeps a 44px tap area.
  const control = cn(COMPOSER_BUTTON, "relative", TAP_AREA);
  const copyButton = (
    <Button type="button" variant="outline" size="sm" className={cn(control, "gap-1.5")} onClick={copy} disabled={unfilled}>
      {copied ? <CheckIcon aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
      {copied ? "Copied" : "Copy Email"}
    </Button>
  );

  const sendButton = (
    <Button
      type="button"
      size="sm"
      className={cn(control, "gap-1.5")}
      onClick={() => (asOwner ? setConfirmingSendAsOwner(true) : onSend(draft))}
      disabled={!canSend}
      aria-busy={sending}
    >
      {sending ? (
        <Loader2Icon className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
      ) : (
        <SendIcon aria-hidden="true" />
      )}
      {sending ? "Sending…" : phase === "failed" ? "Try Again" : asOwner ? `Send as ${owner.name}` : "Send"}
    </Button>
  );
  const notConnected = !!mailbox && !connected;

  return (
    <div
      className={cn(
        sheet ? "flex h-full min-h-0 flex-col" : !reply ? "bg-card rounded-md border px-3 py-2 shadow-xs" : "pt-2",
        className,
      )}
      data-testid="email-composer"
      data-layout={layout}
      aria-busy={sending || undefined}
    >
      {sheet ? (
        // The phone's compact bar: Cancel, who it goes to, and Send (Copy Email while Gmail is not connected).
        <div className="flex h-12 shrink-0 items-center gap-1 border-b px-2" data-testid="email-composer-bar">
          <Button type="button" variant="ghost" size="sm" className={control} onClick={onDiscard} disabled={sending}>
            Cancel
          </Button>
          <h2 className="min-w-0 flex-1 truncate text-center text-[15px] font-semibold">
            Email {to.name?.trim() || to.address}
          </h2>
          {notConnected ? copyButton : sendButton}
        </div>
      ) : !reply ? (
        <div className="flex items-center gap-2">
          <Mail className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
          <h3 className="min-w-0 flex-1 truncate text-sm font-medium">New Email</h3>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-11 sm:size-7"
            onClick={onDiscard}
            disabled={sending}
            aria-label="Close the email"
          >
            <X aria-hidden="true" />
          </Button>
        </div>
      ) : null}

      <div className={cn("flex flex-col gap-2", sheet ? "min-h-0 flex-1 overflow-y-auto px-3 pt-2 pb-2" : !reply && "pl-6")}>
        {notice}
        {pause ? <SuppressionNote info={pause} className="pt-1" /> : null}
        {mailbox && !connected ? (
          <div className="border-warning/40 bg-warning/10 flex flex-col gap-3 rounded-md border p-3 text-sm">
            <p>
              {mailbox.message ?? "Connect your mailbox to send from here."} Until then, copy the email into your
              inbox.
            </p>
            <ConnectMailbox mailbox={mailbox} outlookConnect={outlookConnect} />
          </div>
        ) : null}

        <div
          className={cn(
            "grid grid-cols-[3.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-sm sm:grid-cols-[4rem_minmax(0,1fr)]",
            // Compact on a phone sheet, so the body keeps the room above the keyboard.
            sheet && "shrink-0 gap-y-1",
            asOwner && "border-warning/40 bg-warning/10 -mx-1 rounded-md border px-1 py-1.5",
          )}
        >
          <FieldLabel>{asOwner ? "Sending as" : "From"}</FieldLabel>
          {owner ? (
            <div className="flex min-w-0 items-center gap-2">
              <Select value={sendAs} onValueChange={(v) => onSendAsChange?.(v as "me" | "owner")} disabled={frozen}>
                <SelectTrigger size="sm" className="h-11 w-auto min-w-0 max-w-full sm:h-8" aria-label="Send from">
                  <SelectValue>
                    <span className="truncate">{asOwner ? `${owner.name}'s mailbox` : (from ?? "…")}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="me">{from ?? "Me"}</SelectItem>
                  <SelectItem value="owner" disabled={owner.mailbox.state !== "ready"}>
                    {owner.mailbox.state === "ready" ? `${owner.name}'s mailbox` : `${owner.name}'s mailbox — can't send`}
                  </SelectItem>
                </SelectContent>
              </Select>
              {asOwner ? (
                <Badge variant="outline" className="border-warning/40 bg-warning/10 text-warning shrink-0">
                  not you
                </Badge>
              ) : null}
            </div>
          ) : (
            <span className="truncate">{from ?? "…"}</span>
          )}
          {owner && owner.mailbox.state !== "ready" ? (
            <>
              <span />
              <p className="text-muted-foreground -mt-1 text-xs">
                {owner.mailbox.message ?? `${owner.name} has not connected a mailbox yet.`} You can send as yourself.
              </p>
            </>
          ) : !asOwner && mailbox?.state === "ready" ? (
            <>
              <span />
              <p className="text-muted-foreground -mt-1 text-xs" data-testid="email-composer-sends-from">
                Sends from your {mailboxName(mailbox)}.{mailbox.note ? ` ${mailbox.note}` : ""}
              </p>
            </>
          ) : null}
          <FieldLabel>To</FieldLabel>
          {/* To, Subject and the body take the composers' one field: the to-do and note composers' white box. */}
          <span className={cn(COMPOSER_FIELD, "truncate")}>{[to.name, to.address].filter(Boolean).join(" · ")}</span>
          {!reply ? (
            <>
              <span className="text-muted-foreground self-start pt-2">
                <label htmlFor="email-composer-cc">Cc</label>
              </span>
              <CcInput
                id="email-composer-cc"
                locked={[]}
                value={cc}
                onChange={(next) => set({ cc: next })}
                to={to.address}
                readOnly={frozen}
                onUnfinished={setCcUnfinished}
              />
            </>
          ) : null}
          <FieldLabel htmlFor={reply ? undefined : "email-composer-subject"}>Subject</FieldLabel>
          {reply ? (
            <span className={cn(COMPOSER_FIELD, "text-muted-foreground truncate")} title={subject}>
              {subject}
            </span>
          ) : (
            <Input
              id="email-composer-subject"
              value={subject}
              onChange={(e) => set({ subject: e.target.value })}
              readOnly={frozen}
              // 16px on a phone, so focusing it never zooms iOS Safari; the composers' 14px from `md`.
              className={cn(COMPOSER_FIELD, "h-auto text-base shadow-none md:text-sm")}
            />
          )}
        </div>

        {asOwner ? (
          <Alert className="border-warning/40 bg-warning/10">
            <AlertDescription>
              Sends from {owner.name}&apos;s mailbox ({owner.mailbox.address ?? owner.email}, their{" "}
              {mailboxName(owner.mailbox)}). The customer sees {owner.name}&apos;s address, and the email lands in
              their Sent folder.
            </AlertDescription>
          </Alert>
        ) : null}

        <SnippetTextarea
          aria-label="Email body"
          value={body}
          onChange={(next) => set({ body: next })}
          readOnly={frozen}
          rows={sheet ? 3 : reply ? 6 : 8}
          tenantId={snippets.tenantId}
          brandName={snippets.brandName}
          fills={snippets.fills}
          // `pr-32` clears the Snippets button pinned in the box's top-right corner.
          className={cn(COMPOSER_FIELD, "pr-32 text-base shadow-none md:text-sm", sheet ? "min-h-24 flex-1 resize-none" : "resize-y")}
        />
        {!frozen ? <FieldsToFill value={body} onChange={(next) => set({ body: next })} /> : null}

        {files.length ? (
          <div className="flex flex-wrap gap-1.5">
            {files.map((f) => (
              <FileChip
                key={f.id}
                file={f.file}
                progress={sending ? (progress?.[f.id] ?? 0) : undefined}
                onRemove={frozen ? null : () => set({ files: files.filter((x) => x.id !== f.id) })}
              />
            ))}
          </div>
        ) : null}
        {over > 0 ? (
          <p className="text-destructive flex items-start gap-1.5 text-xs font-medium" role="alert">
            <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
            Attachments total {formatBytes(total)}.{" "}
            {viaOutlook ? "Outlook sends from here take up to 3 MB." : "Gmail sends up to 25 MB."} Remove{" "}
            {formatBytes(over)} to send.
          </p>
        ) : null}

        {tooLong.length ? (
          <p className="text-destructive flex items-start gap-1.5 text-xs font-medium" role="alert">
            <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
            {tooLong.join(" ")} Shorten it to send.
          </p>
        ) : null}

        {sheet ? null : (
          <p className="text-muted-foreground text-xs">
            {signature ? <MailSignature data={signature} compact /> : null} Type / for a snippet.
          </p>
        )}

        {phase === "failed" && error ? (
          <div className="flex items-start gap-2 rounded-md border px-3 py-2 text-sm" role="alert">
            <AlertCircle className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-destructive font-medium">Couldn&apos;t Send</p>
              <p className="text-destructive">{error}</p>
            </div>
          </div>
        ) : null}

        <div className={cn("flex flex-wrap items-center gap-2", sheet && "shrink-0")}>
          <AttachFileButton
            onFiles={addFiles}
            disabled={frozen || files.length >= MAX_FILES}
            label="Attach File"
            className={cn("-ml-2", control)}
          />
          {sheet ? (
            // The phone sheet keeps the hint on the Attach row, so the body reaches nearer the keyboard.
            <p className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
              {signature ? <MailSignature data={signature} compact /> : null} Type / for a snippet.
            </p>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {sheet ? (
              // Send and Cancel are the bar's. A failed send keeps its own Copy and Edit here.
              <>
                {phase === "failed" && !notConnected ? copyButton : null}
                {phase === "failed" && onEditAsNew ? (
                  <Button type="button" variant="ghost" size="sm" className={control} onClick={onEditAsNew}>
                    Edit Email
                  </Button>
                ) : null}
              </>
            ) : notConnected ? (
              <>
                {copyButton}
                <Button type="button" variant="ghost" size="sm" className={control} onClick={onDiscard}>
                  Discard
                </Button>
              </>
            ) : (
              <>
                {phase === "failed" ? copyButton : null}
                {phase === "failed" && onEditAsNew ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={control}
                    onClick={onEditAsNew}
                    title="Change the email and send it as a new one. The failed attempt is not sent again."
                  >
                    Edit Email
                  </Button>
                ) : null}
                <Button type="button" variant="ghost" size="sm" className={control} onClick={onDiscard} disabled={sending}>
                  Discard
                </Button>
                {sendButton}
              </>
            )}
          </div>
        </div>
      </div>
      {asOwner ? (
        <ResponsiveOverlay
          kind="dialog"
          open={confirmingSendAsOwner}
          onOpenChange={setConfirmingSendAsOwner}
          title={`Send as ${owner.name}?`}
          description={`It goes out now from ${owner.name}'s mailbox, ${owner.mailbox.address ?? owner.email}, to ${[to.name, to.address].filter(Boolean).join(" · ")}, exactly as drafted.`}
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setConfirmingSendAsOwner(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setConfirmingSendAsOwner(false);
                  onSend(draft);
                }}
              >
                Send
              </Button>
            </>
          }
        >
          <p className="text-muted-foreground text-sm">
            The customer sees {owner.name}&apos;s address, and the email lands in their Sent folder.
          </p>
        </ResponsiveOverlay>
      ) : null}
    </div>
  );
}
